// TODO: Add image alt tag
/* eslint-disable jsx-a11y/alt-text */
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { ServiceResource, ServiceData, ServiceOsGroup } from '@store/services';
import { PageComponent } from '@root/pages/Type';
import styles from './ServiceListResource.module.scss';
import OsIcon from '@components/Ui/Services/OsIcon';
import classNames from 'classnames';
import ResourceIcon from './Services/ResourceIcon';

type Props = {
    type: string;
    service: ServiceData;
    resource?: ServiceResource;
    isDummyRes?: boolean;
};

type ResourceImageProps = {
    type: string;
    group?: ServiceOsGroup | null;
    className?: string;
};

export const ResourceImage: PageComponent<ResourceImageProps> = ({
    type,
    group,
    className,
}) => {
    if (type === 'os') {
        return <OsIcon name={group?.name} className={className} active />;
    }
    return <ResourceIcon name={type} size={4} className={className} active />;
};

const ServiceListResourceItem: PageComponent<Props> = (props) => {
    const { t, i18n } = useTranslation('uiComponent');

    const { service, resource, isDummyRes = false } = props;

    useEffect(() => {
        i18n.loadNamespaces([`${service.type}_os`]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getValueByType = () => {
        if (isDummyRes) {
            return ' ';
        }
        switch (props.type) {
            case 'os': {
                if (!resource) {
                    return undefined;
                }
                if (resource.value === null) {
                    return t('os_not_found', {
                        defaultValue: 'OS Nepalaikoma',
                        ns: `${service.type}_os`,
                    });
                }
                return t(resource.value, {
                    defaultValue: resource.translate || resource.value,
                    ns: `${service.type}_os`,
                });
            }
            default:
                return resource?.value;
        }
    };

    const getTitleByType = () => {
        if (isDummyRes) {
            return ' ';
        }
        switch (props.type) {
            case 'os': {
                return t('serviceListResource.titles.os', {
                    defaultValue: 'Operacinė sistema',
                });
            }
            case 'ip': {
                return t('serviceListResource.titles.ip', {
                    defaultValue: 'IP adresas',
                });
            }
            case 'cpu': {
                if (service.type === 'vps' || service.type === 'vds') {
                    return t('serviceListResource.titles.vcpu', {
                        defaultValue: 'vCPU',
                    });
                }
                return t('serviceListResource.titles.cpu', {
                    defaultValue: 'CPU',
                });
            }
            case 'ram': {
                return t('serviceListResource.titles.ram', {
                    defaultValue: 'RAM',
                });
            }
            case 'ssd': {
                return t('serviceListResource.titles.ssd', {
                    defaultValue: 'SSD',
                });
            }
            case 'storage': {
                return t('serviceListResource.titles.storage', {
                    defaultValue: 'Diskas',
                });
            }
            case 'traffic': {
                return t('serviceListResource.titles.traffic', {
                    defaultValue: 'Pralaidumas',
                });
            }
            case 'bandwidth': {
                return t('serviceListResource.titles.bandwidth', {
                    defaultValue: 'Srautas',
                });
            }
            default:
                return undefined;
        }
    };

    return (
        <div className={classNames(styles.resource)}>
            <div className={styles.logo_wrapper}>
                <ResourceImage
                    type={props.type}
                    group={
                        resource && 'group' in resource
                            ? resource.group
                            : undefined
                    }
                    className={styles.logo}
                />
            </div>
            <div className={styles.text}>
                <h2>{getTitleByType() || <Skeleton width={76} />}</h2>
                <h3>{getValueByType() || <Skeleton width={76} />}</h3>
            </div>
        </div>
    );
};

type ServiceListResourceProps = {
    service: ServiceData;
};

const ServiceListResource: PageComponent<ServiceListResourceProps> = ({
    service,
}) => {
    const ResourceLongList = ['ip', 'os', 'cpu'];

    const ResourceShortList = [
        'ram',
        service.type !== 'ds' ? 'ssd' : 'storage',
        'traffic',
        'bandwidth',
    ];
    
    return (
        <div className={styles.reource_container}>
            <div className={styles.reource_inner_container}>
                {ResourceLongList.map((resource) => {
                    if (!resource) return null;
                    return (
                        <ServiceListResourceItem
                            key={`list-service-${service.orderNo}-resource-${resource}`}
                            service={service}
                            type={resource}
                            resource={service.resources.find(
                                (_resource) => _resource.type === resource,
                            )}
                        />
                    );
                })}
            </div>
            <div
                className={classNames(
                    styles.reource_inner_container,
                    styles.reource_inner_container_short,
                )}
            >
                {ResourceShortList.map((resource) => {
                    if (!resource) return null;
                    return (
                        <ServiceListResourceItem
                            key={`list-service-${service.orderNo}-resource-${resource}`}
                            service={service}
                            type={resource}
                            resource={service.resources.find(
                                (_resource) => _resource.type === resource,
                            )}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default ServiceListResource;
